import { View } from '@/components/ui/tw-wrappers';
import { track } from '@/lib/firebase';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';

import { useRestrictions } from '@/hooks/use-restrictions';
import { useScenarioStore } from '@/stores/scenario-store';
import { getScenarioSchema, type ScenarioFormValues } from '@/types/scenario.schema';

import { Text } from '@/components/text';
import { Button } from '@/components/ui/button';

import { CountrySelect } from '@/components/country-select';
import { MultiCountrySelect } from '@/components/multi-country-select';
import { NumberInput } from '@/components/ui/number-input';
import { Select } from '@/components/ui/select';
import { Colors } from '@/constants/theme';

import { CONCEPTS, SCENARIO_TYPES } from '@/constants/scenarios';
import { Country } from '@/types/country';
import { ScenarioType } from '@/types/scenario';

export function OnboardingSection({
  onComplete,
  countries,
  countriesLoading,
  countriesError,
}: {
  onComplete: () => void;
  countries: Country[];
  countriesLoading?: boolean;
  countriesError?: string | null;
}) {
  const { t } = useTranslation();
  const SCENARIO_OPTIONS: { id: ScenarioType; label: string }[] = SCENARIO_TYPES.map(type => ({
    id: type,
    label: t(`scenarios.types.${type}.title`),
  }));

  const storeInputs = useScenarioStore(s => s.inputs);
  const setInputs = useScenarioStore(s => s.setInputs);

  const { blockedSet } = useRestrictions();
  const blockedIds = useMemo(() => Array.from(blockedSet), [blockedSet]);

  const schema = useMemo(() => getScenarioSchema(t), [t]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
  } = useForm<ScenarioFormValues>({
    resolver: zodResolver(schema),
    defaultValues: storeInputs as any,
    mode: 'onTouched',
  });

  const vendorIdList = watch('vendorIdList');
  const customerId = watch('customerId');
  const vendorId = watch('vendorId');
  const invoicingCurrency = watch('invoicingCurrency');

  const onRun = handleSubmit(values => {
    track('model_comparison_onboarding_completed', {
      customer: values.customerId,
      vendor: values.vendorId,
      concept: values.invoicingConcept,
      sellThroughLocalPartner: values.sellThroughLocalPartner,
      partnerMargin: values.sellThroughLocalPartner ? values.partnerMargin : null,
      invoicingCurrency: values.invoicingCurrency,
      invoicingAmount: values.invoicingAmount,
    });
    setInputs(values);
    onComplete();
  });

  return (
    <Animated.View entering={FadeIn} style={styles.container}>
      <View style={styles.content}>
        {/* Main Header */}
        {/* <View style={styles.header}>
          <Text style={styles.mainTitle} type='defaultBold'>
            {t('onboarding.title', 'Expansion Setup')}
          </Text>
        </View> */}
        <View style={styles.mainBody} className='flex-col gap-10 lg:flex-row lg:gap-0'>
          {/* Left Column: Market Scope */}
          <View style={styles.columnPart} className='flex-none lg:flex-1'>
            {/* vendorIdList */}
            <View style={styles.fieldGroup}>
              {countriesError ? (
                <View style={styles.inlineError}>
                  <MaterialIcons name='error-outline' size={20} color={Colors.light.error} />
                  <Text style={styles.inlineErrorText}>{countriesError}</Text>
                </View>
              ) : null}
              <Controller
                control={control}
                name='vendorIdList'
                render={({ field: { value, onChange } }) => (
                  <MultiCountrySelect
                    id='vendorIdList'
                    name='vendorIdList'
                    label={t('onboarding.fields.vendorCountryList')}
                    tooltip={t('onboarding.fields.vendorCountryListTooltip')}
                    countries={countries}
                    valueIds={value || []}
                    disabled={countriesLoading}
                    excludeIds={customerId ? [customerId] : []}
                    // disabledIds={blockedIds}
                    onChange={ids => {
                      onChange(ids);
                      if (ids.length > 0) clearErrors('vendorIdList');

                      // Auto logic for the single selection
                      if (ids.length === 1) {
                        setValue('vendorId', ids[0], { shouldValidate: true });
                      } else if (ids.length === 0) {
                        setValue('vendorId', '', { shouldValidate: true });
                      } else {
                        // If multiple, ensure current customerId is in the list
                        if (vendorId && !ids.includes(vendorId)) {
                          setValue('vendorId', '', { shouldValidate: true });
                        }
                      }
                    }}
                  />
                )}
              />
              {errors.vendorIdList ? <Text style={styles.errorText}>{errors.vendorIdList.message}</Text> : null}
            </View>
            {/* vendorId - Revenue Recognition Country */}
            {vendorIdList?.length > 1 && (
              <View style={styles.fieldGroup}>
                <Controller
                  control={control}
                  name='vendorId'
                  render={({ field: { value, onChange } }) => (
                    <CountrySelect
                      id='vendorId'
                      name='vendorId'
                      label={t('onboarding.fields.vendorCountry')}
                      tooltip={t('onboarding.fields.vendorCountryTooltip')}
                      countries={countries.filter(c => vendorIdList?.includes(c.id))}
                      valueId={value || null}
                      disabled={countriesLoading}
                      excludeIds={customerId ? [customerId] : []}
                      // excludeIds={vendorIdList}
                      hideClearButton
                      // disabledIds={blockedIds}
                      placeholder={t('onboarding.fields.searchPlaceholder', 'Search destination…')}
                      onChange={c => {
                        const id = c?.id ?? '';
                        onChange(id);
                        if (id) clearErrors('vendorId');
                      }}
                    />
                  )}
                />
                {errors.vendorId ? <Text style={styles.errorText}>{errors.vendorId.message}</Text> : null}
              </View>
            )}
            {/* customerId  - Expansion country */}
            <View>
              <Controller
                control={control}
                name='customerId'
                render={({ field: { value, onChange } }) => (
                  <CountrySelect
                    id='customerId'
                    name='customerId'
                    label={t('onboarding.fields.customerCountry')}
                    tooltip={t('onboarding.fields.customerCountryTooltip')}
                    hideClearButton
                    disabledIds={blockedIds}
                    countries={countries}
                    valueId={value || null}
                    disabled={countriesLoading}
                    excludeIds={vendorIdList}
                    onChange={c => {
                      const id = c?.id ?? '';
                      onChange(id);
                      if (id) clearErrors('customerId');
                      if (vendorId === id) {
                        setValue('vendorId', '', { shouldValidate: true });
                      }
                    }}
                  />
                )}
              />
              {errors.customerId ? <Text style={styles.errorText}>{errors.customerId.message}</Text> : null}
            </View>

            {/* Select Scenarios */}
            {/* <View style={styles.fieldGroup}>
              <Controller
                control={control}
                name='selectedScenarios'
                render={({ field: { value, onChange } }) => (
                  <MultiSelectChips
                    label={t('onboarding.fields.selectScenarios')}
                    tooltip={t('onboarding.fields.selectScenariosTooltip')}
                    options={SCENARIO_OPTIONS}
                    selectedIds={value || []}
                    onChange={onChange}
                  />
                )}
              />
            </View> */}
          </View>

          {/* Right Column: Financial Parameters */}
          <View style={styles.columnPart} className='flex-none lg:flex-1'>
            {/* <View style={styles.fieldGroup}>
              <Controller
                control={control}
                name='sellThroughLocalPartner'
                render={({ field: { value, onChange } }) => (
                  <Switch
                    label={t('onboarding.fields.localPartnerQuestion', 'Are you selling through a Local Partner?')}
                    value={value}
                    onChange={onChange}

                  />
                )}
              />
            </View> */}
            {/* Business & Industry */}
            <View style={styles.fieldGroup}>
              <Controller
                control={control}
                name='invoicingConcept'
                render={({ field: { value, onChange } }) => (
                  <Select
                    id='invoicingConcept'
                    name='invoicingConcept'
                    label={t('onboarding.fields.invoicingConcept')}
                    tooltip={t('onboarding.fields.invoicingConceptTooltip')}
                    value={value}
                    onValueChange={onChange}
                    options={CONCEPTS.map(c => ({
                      value: c.key,
                      label: t(`${c.i18nKey}.label`),
                      tooltip: t(`${c.i18nKey}.description`),
                    }))}
                    placeholder={t('common.select', 'Select...')}
                  />
                )}
              />
              {errors.invoicingConcept ? <Text style={styles.errorText}>{errors.invoicingConcept.message}</Text> : null}
            </View>

            {/* Partner Margin */}
            {/* <View style={styles.fieldGroup}>
              <Text style={styles.fieldLabel}>
                {t('onboarding.fields.partnerMargin')}
              </Text>
              <Controller
                control={control}
                name='partnerMargin'
                render={({ field: { value, onChange } }) => (
                  <View style={styles.sliderRow}>
                    <View style={styles.valueBox}>
                      <Text style={styles.valueText}>{value}%</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Slider value={value} onChange={onChange} />
                    </View>
                  </View>
                )}
              />
            </View> */}

            {/* invoicing */}
            <View style={styles.fieldGroup}>
              <Text style={styles.fieldLabel}>
                {t('onboarding.fields.invoicingAmount', 'What is the estimated budget and in what currency?')}
              </Text>
              <View style={styles.budgetRow}>
                <Controller
                  control={control}
                  name='invoicingAmount'
                  render={({ field: { value, onChange } }) => (
                    <NumberInput
                      id='invoicingAmount'
                      name='invoicingAmount'
                      style={{ flex: 1 }}
                      value={value}
                      onChange={onChange}
                      prefix={invoicingCurrency}
                      placeholder='50.000'
                    />
                  )}
                />
                {/* <Controller
                  control={control}
                  name='invoicingCurrency'
                  render={({ field: { value, onChange } }) => (
                    <View style={styles.currencySelectWrap}>
                      <Select
                        id='invoicingCurrency'
                        name='invoicingCurrency'
                        value={value}
                        onValueChange={onChange}
                        options={
                          currentCustomer?.currencyISO && currentCustomer?.currencyISO !== 'USD'
                            ? [
                              { value: 'USD', label: 'USD' },
                              {
                                value: currentCustomer?.currencyISO,
                                label: currentCustomer?.currencyISO,
                              },
                            ]
                            : [{ value: 'USD', label: 'USD' }]
                        }
                      />
                    </View>
                  )}
                /> */}
              </View>
              {errors.invoicingAmount && <Text style={styles.errorText}>{errors.invoicingAmount.message}</Text>}
            </View>
          </View>
        </View>
        <View style={styles.footer}>
          <View style={styles.footerAction}>
            <Button
              label={t('onboarding.actions.continue', 'Continue to the simulation')}
              onPress={onRun}
              style={styles.runButton}
            />
          </View>
        </View>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
    marginBottom: 40,
    paddingHorizontal: 20,
  },
  content: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 40,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.05)',
    shadowColor: Colors.light.shadow,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 2,
    overflow: 'hidden',
  },
  header: {
    justifyContent: 'center',
    paddingHorizontal: 30,
    backgroundColor: '#eaeff4',
    paddingVertical: 30,
  },
  mainTitle: {
    fontSize: 28,
  },
  mainBody: {
    paddingHorizontal: 30,
    paddingVertical: 30,
  },
  columnPart: {
    paddingHorizontal: 20,
    gap: 24,
  },

  fieldGroup: {
    gap: 10,
  },
  fieldLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0B5FFF',
  },
  errorText: {
    color: Colors.light.error,
    fontSize: 12,
    marginTop: -4,
  },
  inlineError: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.2)',
    marginBottom: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    justifyContent: 'center',
  },
  inlineErrorText: {
    color: Colors.light.error,
    fontSize: 14,
    textAlign: 'center',
    fontWeight: '500',
  },
  sliderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  valueBox: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#898b8c',
    minWidth: 70,
    alignItems: 'center',
  },
  valueText: {
    fontSize: 14,
    fontWeight: '600',
  },
  budgetRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    width: '100%',
    flexWrap: 'wrap',
  },
  currencySelectWrap: {
    width: 100,
  },
  footer: {
    paddingHorizontal: 30,
    paddingBottom: 30,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  footerAction: {},
  runButton: {
    paddingHorizontal: 32,
    height: 44,
    borderRadius: 12,
  },
});
